/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: extensionsActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../config/api';

/**
 * Shared helpers for Extensions module actions (used in Grid + Form views).
 */

/**
 * Build the URL used to duplicate an extension in the form.
 * Pattern: /pbx/extensions/extensions/new?duplicate={id}
 */
export const getExtensionDuplicateUrl = (id: string | number): string =>
  `/pbx/extensions/extensions/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given extension id.
 */
export const navigateToExtensionDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getExtensionDuplicateUrl(id));
};

/**
 * Low-level API helper to delete an extension by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deleteExtensionById = async (id: string | number): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`/api/telephony/extensions/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete extension';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};


